import { useTranslation } from 'react-i18next';
import { ExternalLink, Star, Search, ShoppingBag } from 'lucide-react';
import ImpressionTracker from './ImpressionTracker';

const TIER_MAP = {
  nl: { best: 'Beste keuze', better: 'Goede keuze', good: 'Optie' },
  en: { best: 'Best pick', better: 'Good pick', good: 'Option' },
};

const TIER_STYLE = {
  best: 'bg-green-600 text-white',
  better: 'bg-blue-600 text-white',
  good: 'bg-muted text-muted-foreground',
};

function trackClick(product, reportId, country, region) {
  fetch(`/api/track/click`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      report_id: reportId || '',
      product_id: product.product_id,
      product_name: product.product_name || '',
      category: product.category || '',
      measure_key: product.measure_key || '',
      merchant: product.manufacturer || '',
      affiliate_link: product.affiliate_link || '',
      country: country || '',
      region: region || '',
    }),
  }).catch(() => {});
}

export default function MeasureProducts({ products, reportId, country, region, onImpression }) {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  if (!products || products.length === 0) return null;

  const tierLabels = TIER_MAP[lang] || TIER_MAP.en;
  const headingLabel = lang === 'nl' ? 'Direct te bestellen' : 'Order directly';
  const viewLabel = lang === 'nl' ? 'Bekijk product' : 'View product';
  const searchLabel = lang === 'nl' ? 'Zoek op bol.com' : 'Search on bol.com';

  return (
    <div className="mt-4 pt-4 border-t-2 border-green-200" data-testid="measure-products">
      <div className="flex items-center gap-2 mb-3">
        <ShoppingBag className="w-4 h-4 text-green-700" />
        <p className="text-sm font-semibold text-green-800">{headingLabel}</p>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3" data-testid="affiliate-disclosure-inline">
        {lang === 'nl'
          ? 'Sommige links hieronder zijn affiliate-links. Wij kunnen een vergoeding ontvangen, zonder extra kosten voor u.'
          : lang === 'de'
          ? 'Einige Links unten sind Affiliate-Links. Wir können eine Provision erhalten, ohne zusätzliche Kosten für Sie.'
          : lang === 'fr'
          ? 'Certains liens ci-dessous sont des liens affiliés. Nous pouvons percevoir une commission, sans coût supplémentaire pour vous.'
          : 'Some links below are affiliate links. We may receive a commission, at no extra cost to you.'}
      </p>
      <div className="space-y-3">
        {products.map(p => (
          <ImpressionTracker key={p.product_id} productId={p.product_id} category={p.category} onImpression={onImpression}>
            <div className="bg-green-50/60 border border-green-200 rounded-xl p-4" data-testid={`product-card-${p.product_id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${TIER_STYLE[p.tier] || TIER_STYLE.good}`}>
                      {tierLabels[p.tier] || p.tier}
                    </span>
                    {p.review_score && (
                      <span className="flex items-center gap-0.5 text-amber-600 text-xs">
                        <Star className="w-3 h-3 fill-amber-400" />
                        {p.review_score}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-foreground">{p.product_name}</p>
                  <p className="text-xs text-muted-foreground">{p.manufacturer}{p.retail_price_eur ? ` · €${p.retail_price_eur.toLocaleString()}` : ''}</p>
                </div>
              </div>
              {/* Product reason — why this fits */}
              {p.reason && (
                <p className="text-xs text-green-800 mb-3 leading-relaxed">{p.reason}</p>
              )}
              {/* Prominent action button — always use affiliate_link from curated/DB */}
              <a href={p.affiliate_link || `https://www.bol.com/nl/nl/s/?searchtext=${encodeURIComponent(p.product_name)}`}
                target="_blank" rel="noopener noreferrer"
                onClick={() => trackClick(p, reportId, country, region)}
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-semibold bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors"
                data-testid={`product-link-${p.product_id}`}>
                {viewLabel} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </ImpressionTracker>
        ))}
      </div>
    </div>
  );
}
